import React, {useEffect, useState} from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import Button from '../common/Button';
import FormFooter from '../common/FormFooter';
import Input from '../common/Input';
import Spinner from '../common/Spinner';

import {postSmsCode, sendSmsCode, postPhoneNumber} from '../../api/api';
import useScrollTop from '../../hooks/useScrollTop';
import {doValidation} from '../../helpers/form-validation';
import {formatDateInput} from '../../helpers/format-helper';
import {formSteps, validations} from '../../constants/form';
import {useDataProviderState, useDataProviderActions} from '../../providers/DataProvider';
import HighlightBox from "../common/HighlightBox.jsx";
import Link from "@mui/material/Link";
import {TrackGoogleAnalyticsEvent} from "../../google-analytics/gaTracker.js";

export default function SmsCodeStep() {

    const {uid, data, userData, loading} = useDataProviderState();
    const {
        nextStep,
        saveData,
        setUserData,
        setLoading,
        setErrorMessage,
        setSuccessMessage,
        setErrorPage
    } = useDataProviderActions();

    const [formData, setFormData] = useState(data[formSteps.SMS_CODE]);
    const [phoneFormData, setPhoneFormData] = useState({telMovil: ''});
    const [errors, setErrors] = useState({});
    const [sendAgainVisibile, setSendAgainVisible] = useState(false);

    const formValidations = validations[formSteps.SMS_CODE];
    const phoneFormValidations = validations[formSteps.ID_PHONE];

    const {nombresApellidosMask, telMovilMask} = userData;

    useScrollTop();

    TrackGoogleAnalyticsEvent("SmsCodeStep");

    function handleFieldChange(event) {
        let {value, name, type} = event.target;

        if (name === 'userFechaNac') {
            value = formatDateInput(value);
        }

        setFormData({...formData, [name]: value});

        if (errors[name] || type === 'select-one') {
            updateErrors(event, name);
        }
    }

    function handlePhoneFieldChange(event) {
        let {value, name} = event.target;
        setPhoneFormData({[name]: value});
        setErrors({[name]: doValidation(event, phoneFormValidations[name])});
    }

    function handleFieldBlur(event) {
        let {name} = event.target;
        updateErrors(event, name);
    }

    function handlePhoneFieldBlur(event) {
        let {name} = event.target;
        setErrors({[name]: doValidation(event, phoneFormValidations[name])});
    }

    function updateErrors(event, name) {
        setErrors({
            ...errors,
            [name]: doValidation(event, formValidations[name]),
        });
    }

    function isFormValid() {
        const updatedErrors = {
            userCodigoValidacion: doValidation({target: {value: formData.userCodigoValidacion}}, formValidations.userCodigoValidacion),
            userFechaNac: doValidation({target: {value: formData.userFechaNac}}, formValidations.userFechaNac),
        };

        setErrors(updatedErrors);

        return !Object.values(updatedErrors).some(Boolean);
    }

    function isPhoneFormValid() {
        const updatedErrors = {
            telMovil: doValidation({target: {value: phoneFormData.telMovil}}, phoneFormValidations.telMovil)
        };

        setErrors(updatedErrors);

        return !Object.values(updatedErrors).some(Boolean);
    }

    async function handleNext() {
        if (isFormValid()) {
            setLoading(true);
            setErrors({});

            // clear messages
            setSuccessMessage(null);
            setErrorMessage(null);

            const response = await postSmsCode(formData, uid);

            if (response?.success) {
                if (response.data?.status !== 'rojo') {
                    saveData(formData, formSteps.SMS_CODE);
                    setUserData({
                        nombre: response.data.nombre,
                        telMovil: response.data.telMovil,
                        status: response.data.status,
                    })
                    history.pushState(null, null, `#${response.data.uid}`);
                    nextStep();
                } else {
                    setErrorPage(response.data.statusMessage);
                }
            } else if (response?.errors) {
                setErrors(response.errors);
            } else {
                setErrorMessage();
            }

            setLoading(false);
        }
    }

    async function handleSendCodeAgain() {
        setLoading(true);
        const response = await sendSmsCode(uid);

        if (response?.success) {
            setSuccessMessage('El código se ha enviado exitosamente.')
        } else if (response?.errors) {
            setErrors(response.errors);
        } else {
            setErrorMessage();
        }

        setLoading(false);
    }

    async function handleUpdatePhoneNumber() {
        setLoading(true);
        setErrors({});

        if (isPhoneFormValid()) {
            const response = await postPhoneNumber(phoneFormData, uid);

            if (response?.success) {
                setUserData(response.data);
                setSuccessMessage('El número de teléfono se ha actualizado exitosamente.');
                setSendAgainVisible(false);
            } else if (response?.errors) {
                setErrors(response.errors);
            } else {
                setErrorMessage();
            }
        }

        setLoading(false);
    }

    return (
        <Container component="section" maxWidth="sm">

            {!sendAgainVisibile ? (
                <>
                    <Box sx={{pb: 2}}>
                        <HighlightBox>
                            <Box sx={{display: 'flex'}}>
                                <Box sx={{py: 2}}>
                                    <Box className="color-primary" sx={{pl: 3, pr: 3, pb: 1, fontSize: '1.3rem'}}>
                                        <b>Hola {nombresApellidosMask},</b>
                                    </Box>
                                    <Box as="span" sx={{
                                        display: 'inline-block',
                                        px: 2,
                                        color: 'white',
                                        background: 'var(--color-primary)',
                                        borderBottomRightRadius: '15px',
                                        borderTopRightRadius: '15px',
                                        fontSize: '1.2rem'
                                    }}>
                                        ¡Felicitaciones, tu préstamo está Pre-Aprobado!
                                    </Box>
                                    <Box sx={{p: 1, pl: 2}}>
                                        <b className="msg">Para avanzar te vamos a pedir que ingreses el código que llegó a tu celular {telMovilMask} y tu fecha de nacimiento.</b>
                                    </Box>
                                </Box>
                            </Box>
                        </HighlightBox>
                    </Box>

                    <Grid container spacing={{xs: 0, sm: 2}} alignItems="center" justifyContent="center">
                        <Grid item sm={6} xs={12} sx={{pb: 1}} alignItems="center" justifyContent="center">
                            <Grid item sm={12} xs={12} sx={{pb: 3}}>
                                <Input
                                    label="Código recibido por SMS"
                                    name="userCodigoValidacion"
                                    placeholder="0000"
                                    formData={formData}
                                    errors={errors}
                                    formValidations={formValidations}
                                    handleFieldChange={handleFieldChange}
                                    handleFieldBlur={handleFieldBlur}
                                />
                            </Grid>
                            <Grid item sm={12} xs={12} sx={{pb: 0}}>
                                <Box sx={{pl: 1, mb: 1}} justifyContent="center">
                                    ¿No recibiste el SMS con el código?
                                </Box>
                                <Box sx={{pl: 1, mb: 0}} justifyContent="center">
                                    <Link href="#" onClick={handleSendCodeAgain} underline="hover">Enviarlo nuevamente</Link> o <Link href="#" onClick={() => setSendAgainVisible(true)} underline="hover">modificar el número de teléfono</Link>.
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid container spacing={{xs: 0, sm: 2}} alignItems="center"
                          justifyContent="center">
                        <Grid item sm={6} xs={12} sx={{pb: 1}}>
                            <Box sx={{pl: 1, pt: 2, pb: 2}}>¿Nos confirmás tu fecha de nacimiento?</Box>
                            <Input
                                label="Fecha de nacimiento"
                                name="userFechaNac"
                                placeholder="dd/mm/aaaa"
                                formData={formData}
                                errors={errors}
                                formValidations={formValidations}
                                handleFieldChange={handleFieldChange}
                                handleFieldBlur={handleFieldBlur}
                            />
                        </Grid>
                    </Grid>

                    <FormFooter
                        hideBackButton
                        onNext={handleNext}
                    />
                </>
            ) : (
                <>
                    <h3>Cambiá tu número de celular</h3>
                    <Input
                        label="¿Cuál es tu celular?"
                        name="telMovil"
                        placeholder="Ej: 099000000"
                        formData={phoneFormData}
                        errors={errors}
                        formValidations={phoneFormValidations}
                        handleFieldChange={handlePhoneFieldChange}
                        handleFieldBlur={handlePhoneFieldBlur}
                    />
                    <FormFooter
                        nextButtonLabel="actualizar"
                        onNext={handleUpdatePhoneNumber}
                        onPrev={() => setSendAgainVisible(false)}
                    />
                </>
            )}

            <Spinner loading={loading}/>

        </Container>
    )

}
